// Base64URL <-> ArrayBuffer helpers for the WebAuthn ceremony.
// See MDN + py_webauthn — challenge / user.id / cred.id are exchanged as
// base64url strings but navigator.credentials.* needs BufferSource types.

export function bufferToBase64Url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary)
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

export function base64UrlToBuffer(base64url) {
  let s = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const pad = s.length % 4;
  if (pad === 2) s += "==";
  else if (pad === 3) s += "=";
  else if (pad !== 0) s += "===";
  const binary = window.atob(s);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

// Convert the JSON creation options returned by the backend into the shape
// navigator.credentials.create() expects.
export function decodeCreationOptions(publicKey) {
  const opts = {
    ...publicKey,
    challenge: base64UrlToBuffer(publicKey.challenge),
    user: { ...publicKey.user, id: base64UrlToBuffer(publicKey.user.id) },
  };
  if (Array.isArray(publicKey.excludeCredentials)) {
    opts.excludeCredentials = publicKey.excludeCredentials.map((c) => ({
      ...c, id: base64UrlToBuffer(c.id),
    }));
  }
  return opts;
}

export function decodeRequestOptions(publicKey) {
  const opts = { ...publicKey, challenge: base64UrlToBuffer(publicKey.challenge) };
  if (Array.isArray(publicKey.allowCredentials)) {
    opts.allowCredentials = publicKey.allowCredentials.map((c) => ({
      ...c, id: base64UrlToBuffer(c.id),
    }));
  }
  return opts;
}

// Assemble the JSON payload the backend expects after credentials.create().
export function encodeRegistrationCredential(credential) {
  const r = credential.response;
  return {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: bufferToBase64Url(r.attestationObject),
      clientDataJSON: bufferToBase64Url(r.clientDataJSON),
      transports: typeof r.getTransports === "function" ? r.getTransports() : [],
    },
    clientExtensionResults:
      typeof credential.getClientExtensionResults === "function"
        ? credential.getClientExtensionResults()
        : {},
    authenticatorAttachment: credential.authenticatorAttachment || null,
  };
}

// Assemble the JSON payload the backend expects after credentials.get().
export function encodeAssertion(credential) {
  const r = credential.response;
  return {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      authenticatorData: bufferToBase64Url(r.authenticatorData),
      clientDataJSON: bufferToBase64Url(r.clientDataJSON),
      signature: bufferToBase64Url(r.signature),
      userHandle: r.userHandle ? bufferToBase64Url(r.userHandle) : null,
    },
    clientExtensionResults:
      typeof credential.getClientExtensionResults === "function"
        ? credential.getClientExtensionResults()
        : {},
    authenticatorAttachment: credential.authenticatorAttachment || null,
  };
}

export function webauthnSupported() {
  return typeof window !== "undefined"
    && !!window.PublicKeyCredential
    && !!(navigator.credentials && navigator.credentials.create);
}
